import {
	Box,
	createStyles,
	Divider,
	Flex,
	Group,
	Skeleton,
	Space,
	Stack,
} from '@mantine/core';
import type { PolicyOut } from '@repo/api-codegen';
import { useLocalStorageBooleanState } from '@repo/common/hooks/useLocalStorageBooleanState';
import { Banner, Switch, Text } from '@repo/foundations';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
	useAutomation,
	useUpdateAutomation,
} from '../../../../api/hooks/automation';
import { RichEditor } from '../../../../components/RichEditor';
import { SecodaEntityIcon } from '../../../../components/SecodaEntity/SecodaEntityIcon';
import PolicySection from '../../PolicySection';

interface PolicyRemediationProps {
	policy: PolicyOut;
	readOnly?: boolean;
}

const useStyles = createStyles((theme) => ({
	editorWrapper: {
		'& .ProseMirror': {
			fontSize: theme.fontSizes.sm,
		},
	},
}));

export default function PolicyRemediation({
	policy,
	readOnly = false,
}: PolicyRemediationProps) {
	const { classes } = useStyles();
	const navigate = useNavigate();
	const [
		showConnectAutomationBanner,
		{ setTrue: dismissConnectAutomationBanner },
	] = useLocalStorageBooleanState('policyConnectAutomationBannerShown', false);

	const { data: automation, isLoading: isAutomationLoading } = useAutomation({
		id: policy.automation_id ?? '',
		options: {
			enabled: !!policy.automation_id,
		},
	});

	const { mutateAsync: updateAutomation } = useUpdateAutomation({});

	const handleAutomationStatusToggle = useCallback(
		async (enabled: boolean) => {
			if (automation?.id) {
				await updateAutomation({
					data: {
						id: automation.id,
						is_enabled: enabled,
					},
				});
			}
		},
		[automation?.id, updateAutomation]
	);

	const openEditModal = useCallback(() => {
		navigate(`/policy/${policy.id}/edit`, {
			state: {
				prev_url_path: `/policy/${policy.id}`,
				scrollToBottom: true,
			},
		});
	}, [navigate, policy.id]);

	return (
		<Stack pt="lg">
			{!policy.automation_id && !showConnectAutomationBanner && !readOnly && (
				<Banner
					tone="info"
					message="Connect an automation to your workflow."
					title="Want to automate the way you remediate issues?"
					onDismiss={dismissConnectAutomationBanner}
					actions={[
						{
							children: 'Create automation',
							leftIconName: 'bolt',
							onClick: openEditModal,
						},
					]}
				/>
			)}
			{policy.automation_id && (
				<PolicySection
					content={
						<Box className={classes.editorWrapper}>
							{isAutomationLoading ? (
								<Flex align="center" justify="space-between" w="100%">
									<Group>
										<Skeleton height={36} width={36} radius="md" />
										<Stack spacing={4}>
											<Skeleton height={18} width={180} radius="sm" />
											<Skeleton height={14} width={220} radius="sm" />
										</Stack>
									</Group>
									<Skeleton height={24} width={42} radius="xl" />
								</Flex>
							) : (
								<Box>
									<Group position="apart">
										<Group
											onClick={() => navigate(`/automation/${automation?.id}`)}
											sx={{ cursor: 'pointer' }}
										>
											<SecodaEntityIcon
												icon="bolt"
												showOverlay
												isAutomation
												size={24}
												hoverable
											/>

											<Stack spacing={0}>
												<Text
													size="sm"
													weight="semibold"
													children={automation?.title || 'Automation'}
												/>
												<Text
													size="xs"
													color="text/secondary/default"
													children="Enable automatic remediation."
												/>
											</Stack>
										</Group>
										<Box
											onClick={(event) => {
												event.stopPropagation();
											}}
										>
											<Switch
												checked={automation?.is_enabled ?? false}
												onChange={(event) => {
													handleAutomationStatusToggle(
														event.currentTarget.checked
													);
												}}
												disabled={readOnly}
											/>
										</Box>
									</Group>
									<Space h="sm" />
									<Divider />
									<Space h="sm" />
									<Banner
										tone="info"
										message={`Automation will run next at ${dayjs(automation?.next_run_at).format('dddd[,] h:mm A')}`}
										inCard
									/>
								</Box>
							)}
						</Box>
					}
				/>
			)}
			<PolicySection
				content={
					<Box className={classes.editorWrapper}>
						<RichEditor
							emptyDocPlaceholder="No remediation guidelines provided."
							readOnly
							initialValue={
								policy.remediation || 'No remediation guidelines provided.'
							}
							disableTopGap
							limited
						/>
					</Box>
				}
			/>
		</Stack>
	);
}
